import React, { useState, useEffect } from 'react'
import { Navbar } from '../components/Navbar'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'
import {MapPin, Phone, Clock, ExternalLink, Navigation, MessageCircleDashed as MessageCircle, Building2, AlertCircle} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { detectLocation } from '../utils/geolocation'
import toast from 'react-hot-toast'

interface Hemocenter {
  _id: string
  name: string
  type: string
  street: string
  number: string
  neighborhood: string
  city: string
  state: string
  zipCode: string
  latitude: number
  longitude: number
  phone: string
  whatsapp?: string
  website?: string
  hours: string
  notes?: string
  distance?: number
}

export function HemocentersPage() {
  const [hemocenters, setHemocenters] = useState<Hemocenter[]>([])
  const [filteredCenters, setFilteredCenters] = useState<Hemocenter[]>([])
  const [loading, setLoading] = useState(true)
  const [searchCity, setSearchCity] = useState("")
  const [searchState, setSearchState] = useState("")
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [detectingLocation, setDetectingLocation] = useState(false)

  useEffect(() => {
    fetchHemocenters()
  }, [])

  const fetchHemocenters = async () => {
    try {
      setLoading(true)
      const response = await lumi.entities.hemocenters.list({ limit: 100 })
      setHemocenters(response.list)
      setFilteredCenters(response.list)
    } catch (error) {
      console.error("Failed to fetch hemocenters:", error)
      toast.error("Erro ao carregar hemocentros")
    } finally {
      setLoading(false)
    }
  }

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371 // Earth radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLon = ((lon2 - lon1) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  const handleDetectLocation = async () => {
    setDetectingLocation(true)
    try {
      const location = await detectLocation()
      setUserLocation({ lat: location.latitude, lng: location.longitude })
      setSearchCity(location.city)
      setSearchState(location.state)
      
      // Calculate distances and sort
      const centersWithDistance = hemocenters.map(center => ({
        ...center,
        distance: calculateDistance(location.latitude, location.longitude, center.latitude, center.longitude)
      }))
      
      const sorted = centersWithDistance.sort((a, b) => (a.distance || 0) - (b.distance || 0))
      setFilteredCenters(sorted)
      
      toast.success("Localização detectada! Mostrando hemocentros mais próximos")
    } catch (error) {
      toast.error("Não foi possível detectar sua localização. Por favor, busque manualmente.")
    } finally {
      setDetectingLocation(false)
    }
  }

  const handleSearch = () => {
    if (!searchCity && !searchState) {
      setFilteredCenters(hemocenters)
      return
    }

    const filtered = hemocenters.filter(center => {
      const cityMatch = !searchCity || center.city.toLowerCase().includes(searchCity.toLowerCase())
      const stateMatch = !searchState || center.state === searchState
      return cityMatch && stateMatch
    })

    if (filtered.length === 0 && searchState) {
      // Show all from same state if no exact city match
      const stateFiltered = hemocenters.filter(center => center.state === searchState)
      setFilteredCenters(stateFiltered)
      if (stateFiltered.length > 0) {
        toast("Não encontramos hemocentros nesta cidade, mas você pode doar nestes locais próximos", { icon: "ℹ️" })
      } else {
        toast.error("Nenhum hemocentro encontrado nesta região")
      }
    } else {
      setFilteredCenters(filtered)
    }
  }

  const openGoogleMaps = (center: Hemocenter) => {
    const address = `${center.street}, ${center.number}, ${center.neighborhood}, ${center.city} - ${center.state}`
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank')
  }

  const openWhatsApp = (phone: string) => {
    const cleanPhone = phone.replace(/\D/g, '')
    window.open(`https://wa.me/55${cleanPhone}`, '_blank')
  }

  const brazilianStates = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 via-white to-gray-50">
      <Navbar />

      {/* Header */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <Building2 className="h-16 w-16 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-black">Encontre um Hemocentro</h1>
            <p className="text-lg text-red-100">
              Encontre um local seguro para doar sangue perto de você. Mesmo sem um pedido específico, cada doação fortalece os estoques e salva vidas.
            </p>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Buscar por localização</h2>
            
            <div className="space-y-4">
              <Button 
                onClick={handleDetectLocation}
                disabled={detectingLocation}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white h-14 text-lg font-semibold"
              >
                <Navigation className={`h-5 w-5 mr-3 ${detectingLocation ? 'animate-spin' : ''}`} />
                {detectingLocation ? 'Detectando localização...' : 'Usar minha localização atual'}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium">ou busque manualmente</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Input
                    label="Cidade"
                    value={searchCity}
                    onChange={(e) => setSearchCity(e.target.value)}
                    placeholder="Ex: São Paulo"
                  />
                </div>
                <div>
                  <Select
                    label="Estado"
                    value={searchState}
                    onChange={(e) => setSearchState(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {brazilianStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </Select>
                </div>
              </div>

              <Button 
                onClick={handleSearch}
                variant="outline"
                className="w-full border-2 border-red-600 text-red-600 hover:bg-red-50 h-12 font-semibold"
              >
                <MapPin className="h-5 w-5 mr-2" />
                Buscar Hemocentros
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Carregando hemocentros...</p>
            </div>
          ) : filteredCenters.length === 0 ? (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-8 text-center">
              <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Nenhum hemocentro encontrado</h3>
              <p className="text-gray-600">Tente buscar por outra cidade ou estado</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {filteredCenters.length} {filteredCenters.length === 1 ? 'hemocentro encontrado' : 'hemocentros encontrados'}
                </h2>
                {userLocation && (
                  <p className="text-sm text-gray-600 mt-1">Ordenados por distância da sua localização</p>
                )}
              </div>

              <div className="space-y-4">
                {filteredCenters.map((center, index) => (
                  <div 
                    key={center._id} 
                    className="bg-white rounded-2xl shadow-md border border-gray-200 p-6 hover:shadow-xl transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-gray-900">{center.name}</h3>
                          {index < 3 && center.distance && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm">
                              Mais próximo de você
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 font-medium">{center.type}</p>
                      </div>
                      {center.distance && (
                        <div className="text-right">
                          <p className="text-2xl font-black text-red-600">{center.distance.toFixed(1)} km</p>
                          <p className="text-xs text-gray-500">de distância</p>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-start gap-2 text-gray-700">
                        <MapPin className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">
                          {center.street}, {center.number} - {center.neighborhood}<br />
                          {center.city} - {center.state}, CEP {center.zipCode}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 text-gray-700">
                        <Clock className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        <p className="text-sm font-medium">{center.hours}</p>
                      </div>

                      <div className="flex items-center gap-2 text-gray-700">
                        <Phone className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <p className="text-sm font-medium">{center.phone}</p>
                      </div>

                      {center.notes && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                          <p className="text-sm text-blue-900"><strong>Observações:</strong> {center.notes}</p>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Button
                        onClick={() => openGoogleMaps(center)}
                        variant="outline"
                        className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Ver no Mapa
                      </Button>

                      <Button
                        onClick={() => window.open(`tel:${center.phone}`, '_self')}
                        variant="outline"
                        className="border-2 border-green-600 text-green-600 hover:bg-green-50 font-semibold"
                      >
                        <Phone className="h-4 w-4 mr-2" />
                        Ligar
                      </Button>

                      {center.whatsapp && (
                        <Button
                          onClick={() => openWhatsApp(center.whatsapp!)}
                          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold"
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          WhatsApp
                        </Button>
                      )}

                      {center.website && !center.whatsapp && (
                        <Button
                          onClick={() => window.open(center.website, '_blank')}
                          variant="outline"
                          className="border-2 border-gray-400 text-gray-700 hover:bg-gray-50 font-semibold"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Site
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center text-white space-y-4">
            <h2 className="text-3xl font-black">Pronto para doar?</h2>
            <p className="text-red-100 text-lg">
              Entre em contato com o hemocentro mais próximo e ajude a salvar vidas hoje mesmo!
            </p>
            <div className="bg-red-800/50 rounded-xl p-4 mt-6">
              <p className="text-sm text-red-100">
                <strong>Dica:</strong> Ligue antes para confirmar horários e verificar se é necessário agendamento prévio.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
